import React, { useState, useEffect, useMemo } from 'react';
import {
  useDisclosure,
  AccordionItem, AccordionButton, AccordionPanel, AccordionIcon,
  TableContainer, Table, Thead, Tbody, Tr, Th, Td,
  Box,
  FormLabel, Button, IconButton,
  Icon,
} from '@chakra-ui/react'
import { FaCheckCircle } from "react-icons/fa";
import { RiDeleteBin6Fill } from "react-icons/ri";
import { useTranslation } from 'react-i18next';
import AssignModal from '../Approve/AssignModal';
import type { Employee } from '../../contexts/types';

/**
 * 担当者設定パーツ引数情報
 */
interface AssignAccordionItemProps {
  /** タイトル */
  title: string,
  /** 識別ID */
  id: string,
  /** 部署単位の選択担当者設定関数 */
  changeSelectedEmployees: (divisionId: string, selectedEmployees: Employee[]) => void,
  /** 従業員マスタ情報の絞り込み条件（組織分類IDリスト） */
  conditionOrganizationClassIds?: string[],
  /** 従業員マスタ情報の絞り込み条件（親組織IDリスト） */
  conditionParentOrganizationIds?: string[],
  /** 従業員マスタ情報の絞り込み条件（子孫組織の組織分類IDリスト） */
  conditionDescendantOrganizationClassIds?: string[],
  /** 従業員マスタ情報 */
  employees: Employee[];
  /** 初期選択状態の従業員ID */
  initSelectedEmployeeIds?: string[];
  /** 非活性従業員の従業員IDリスト */
  disabledEmployeeIds: string[];
  /** 最大選択人数（0 = 無制限、デフォルト: 0） */
  maxAssignees?: number;
}

/**
 * 担当者設定パーツ
 * @param title タイトル
 * @param id 識別ID
 * @param changeSelectedEmployees 部署単位の選択担当者設定関数
 * @param conditionOrganizationClassIds 従業員マスタ情報の絞り込み条件（組織分類IDリスト）
 * @param conditionParentOrganizationIds 従業員マスタ情報の絞り込み条件（親組織IDリスト）
 * @param conditionDescendantOrganizationClassIds 従業員マスタ情報の絞り込み条件（子孫組織の組織分類IDリスト）
 * @param employees 従業員マスタ情報
 */
const AssignAccordionItem: React.FC<AssignAccordionItemProps> = ({ title, id, changeSelectedEmployees, conditionOrganizationClassIds, conditionParentOrganizationIds, conditionDescendantOrganizationClassIds, employees, initSelectedEmployeeIds, disabledEmployeeIds, maxAssignees = 0 }) => {

  /** 翻訳 */
  const { t } = useTranslation();
  /** 各種Modalの制御 */
  const { isOpen: isOpenAssignModal, onOpen: onOpenAssignModal, onClose: onCloseAssignModal } = useDisclosure();

  /** 従業員マスタ情報 */
  const [selectedEmployees, setSelectedEmployees] = useState<Employee[]>([]);

  /** 選択中の従業員IDリスト */
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<string[]>([]);

  /** 従業員マスタ情報（全従業員、同一ユーザーの部署名を連結） */
  const allEmployees: Employee[] = useMemo(() => {
    // employeeIdでグループ化
    const groupedMap = new Map<string, Employee[]>();
    employees.forEach(e => {
      const id = e.employeeId ?? '';
      if (!groupedMap.has(id)) {
        groupedMap.set(id, []);
      }
      groupedMap.get(id)!.push(e);
    });

    // 各グループから1レコードを作成（部署名は連結）
    const result: Employee[] = [];
    groupedMap.forEach((group) => {
      if (group.length === 0) return;
      // 最初のレコードをベースにする
      const base = { ...group[0] };
      // 部署名を連結（重複排除）
      const uniqueDivisionNames = [...new Set(group.map(e => e.divisionName).filter(Boolean))];
      base.divisionName = uniqueDivisionNames.join(' / ');
      result.push(base);
    });

    return result;
  }, [employees]);

  /**
   * 初期選択状態を親から受け取る
   */
  useEffect(() => {
    if (!Array.isArray(initSelectedEmployeeIds)) {
      console.error("initSelectedEmployeeIds is not an array or is undefined:", initSelectedEmployeeIds);
      return;
    }
    // undefinedを除外
    const validInitSelectedEmployeeIds = initSelectedEmployeeIds.filter(
      (id): id is string => id !== undefined
    );
    // allEmployees（重複排除済み）から従業員情報を抽出
    const initSelectedEmployees = allEmployees.filter((e) =>
      validInitSelectedEmployeeIds.includes(e.employeeId ?? '')
    );
    setSelectedEmployees(initSelectedEmployees);
    setSelectedEmployeeIds(initSelectedEmployees.map(emp => emp.employeeId ?? ''));
  }, [initSelectedEmployeeIds, allEmployees]);

  /** 担当者設定ポップアップを表示する。 */
  const handleOpenAssignModal = () => {
    setSelectedEmployeeIds(selectedEmployees.map(emp => emp.employeeId ?? ''));
    onOpenAssignModal();
  };

  /**
   * 選択した従業員情報リストを受け取り、従業員マスタ情報に反映する。
   * @param selectedEmployees 選択した従業員情報リスト
   */
  const handleOk = (selectedEmployees: Employee[]) => {
    setSelectedEmployees(selectedEmployees);
    changeSelectedEmployees(id, selectedEmployees);
  }

  /**
   * 「クリア」ボタン押下時に設定中の従業員リストを空にする。
   */
  const handleClear = () => {
    setSelectedEmployees([]);
    changeSelectedEmployees(id, []);
  }

  /**
   * 指定した従業員を選択済みリストから削除する。
   */
  const handleRemoveEmployee = (employeeId: string) => {
    const updated = selectedEmployees.filter(e => e.employeeId !== employeeId);
    setSelectedEmployees(updated);
    changeSelectedEmployees(id, updated);
  }

  return (
    <>
      <AccordionItem>
        <h2>
          <AccordionButton>
            <Box as='span' flex='1' textAlign='left' verticalAlign={'middle'} display="flex" alignItems="center">
              <FormLabel fontWeight={'Bold'} display="flex" alignItems="center">
                <Icon as={FaCheckCircle} boxSize={8} mr={2} color={selectedEmployees.length === 0 ? '#ccc' : 'green'} />
                {/* タイトル */}
                {title}
                {/* 選択中担当者表示 */}
                {selectedEmployees.length > 0 ? `　-　${selectedEmployees[0].employeeName}` : ""}
                {selectedEmployees.length > 1 ? `　${t('features.Approve.assignment.accordion.otherCount', { count: selectedEmployees.length - 1 })}` : ""}
              </FormLabel>
            </Box>
            <AccordionIcon />
          </AccordionButton>
        </h2>
        <AccordionPanel pb={4}>
          <Button colorScheme='green' mb={3} mr={2} onClick={handleOpenAssignModal}>{t('features.Approve.assignment.button.assignHandler')}</Button>
          <Button colorScheme="red" mb={3} onClick={handleClear}>{t('common.button.clear')}</Button>

          {/* 担当者選択有無で分岐 */}
          {selectedEmployees.length === 0 ? (
            <Box>{t('features.Approve.assignment.message.pleaseSetHandler')}</Box>
          ) : (
            <TableContainer border="1px solid" borderColor="gray.200" width="100%" maxHeight="300px" overflowY="unset">
              <Table border="1px solid" borderColor="gray.200" variant="simple" style={{ tableLayout: "fixed" }}>
                <Thead position="sticky" top={0} boxShadow={`0 -1px 0 #EEEEEE`}>
                  <Tr borderRight="1px solid" borderColor="gray.200" bgColor="gray.100">
                    <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.100" width={140}>{t('common.field.departmentName')}</Th>
                    <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.100" width={140}>{t('common.field.employeeName')}</Th>
                    <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.100" width={5}></Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {selectedEmployees.map(employee => (  // 従業員情報で繰り返し
                    <Tr
                      key={employee.employeeId}
                      _hover={{ bg: "blue.200" }} // マウスオーバーで背景色変更
                    >
                      <Td borderRight="1px solid" borderColor="gray.200" title={employee.divisionName} style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {employee.divisionName}
                      </Td>
                      <Td borderRight="1px solid" borderColor="gray.200" title={employee.employeeName} style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {employee.employeeName}
                      </Td>
                      <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                        <IconButton
                          aria-label="Delete"
                          icon={<RiDeleteBin6Fill />}
                          size="sm"
                          colorScheme="red"
                          variant="ghost"
                          onClick={() => handleRemoveEmployee(employee.employeeId ?? '')}
                        />
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          )}
        </AccordionPanel>
      </AccordionItem >
      <AssignModal
        isOpen={isOpenAssignModal}
        onClose={onCloseAssignModal}
        onOk={handleOk}
        title={t('features.Approve.assignment.title.selectHandler')}
        prevSelectedEmployeeIds={selectedEmployeeIds}
        disabledEmployeeIds={disabledEmployeeIds}
        employees={allEmployees}
        maxAssignees={maxAssignees}
      />
    </>
  );
}

export default AssignAccordionItem;